import {
  TEMA_PADRAO,
  temaAplicado,
  temaValido,
  type Tema,
  type TemaAplicado,
} from '@aponte/shared';

/**
 * Aplicação do tema no documento.
 *
 * A escolha vive em três lugares, com papéis diferentes:
 *
 *  - `localStorage` — é o que vale antes de o login acontecer. A tela de entrada não tem
 *    a quem perguntar, e ninguém deveria ver o tema piscar de escuro para claro depois de
 *    digitar a senha.
 *  - API (`/preferencias`) — é o que acompanha a pessoa para outro computador.
 *  - `data-tema` no `<html>` — é o que o CSS enxerga; sempre já resolvido para claro ou
 *    escuro, porque "sistema" é decisão do navegador, não do CSS.
 */
const CHAVE = 'aponte.tema';

/** Cor da barra do navegador no celular, por tema. Combina com o fundo de cada paleta. */
const COR_DA_BARRA: Record<TemaAplicado, string> = {
  escuro: '#0B0906',
  claro: '#F6F1E7',
};

function consultaDoSistema(): MediaQueryList | null {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: light)')
    : null;
}

/** O que o sistema operacional pede agora. Sem `matchMedia`, mantém o padrão da casa. */
export function temaDoSistema(): TemaAplicado {
  return consultaDoSistema()?.matches ? 'claro' : 'escuro';
}

export function lerTemaSalvo(): Tema | null {
  try {
    return temaValido(localStorage.getItem(CHAVE));
  } catch {
    // Navegador com armazenamento bloqueado. Sem preferência local, e sem quebrar a tela.
    return null;
  }
}

export function salvarTema(tema: Tema): void {
  try {
    localStorage.setItem(CHAVE, tema);
  } catch {
    // A escolha vale para esta sessão e, se houver login, ainda assim sobe para a API.
  }
}

/** Pinta o documento. É o único ponto que escreve `data-tema`. */
export function aplicarTema(escolha: Tema): TemaAplicado {
  const aplicado = temaAplicado(escolha, temaDoSistema());

  document.documentElement.dataset.tema = aplicado;
  document.documentElement.style.colorScheme = aplicado === 'claro' ? 'light' : 'dark';
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', COR_DA_BARRA[aplicado]);

  return aplicado;
}

/**
 * Aplica o tema guardado antes de a interface montar.
 *
 * Chamado de `main.tsx`, e não de um script no `index.html`: a CSP do cockpit é
 * `script-src 'self'`, então script inline não roda em produção — e passaria despercebido
 * no desenvolvimento, onde não há CSP nenhuma.
 */
export function aplicarTemaSalvo(): Tema {
  const escolha = lerTemaSalvo() ?? TEMA_PADRAO;
  aplicarTema(escolha);
  return escolha;
}

/**
 * Avisa quando o sistema operacional troca entre claro e escuro. Só interessa a quem
 * escolheu "sistema" — quem fixou um tema não deve ver a tela mudar sozinha ao anoitecer.
 */
export function observarTemaDoSistema(aoMudar: () => void): () => void {
  const consulta = consultaDoSistema();
  if (!consulta) return () => {};

  consulta.addEventListener('change', aoMudar);
  return () => consulta.removeEventListener('change', aoMudar);
}
